import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import { useTheme } from "./components/theme/ThemeProvider";
// EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1
import { I18nProvider } from "./i18n/I18nProvider";
import { PlatformSettingsProvider } from "./components/settings/PlatformSettingsContext";
import { UserSettingsProvider } from "./components/settings/UserSettingsContext";
import { CalendarEditProvider } from "./components/calendar";
// EF-PLATFORM-AUTHENTICATION-V1: Authentication
import { AuthProvider, useAuth } from "./context/AuthContext";
// EF-SUPERADMIN-TENANT-SWITCH-V1: Tenant context switching
import { TenantContextProvider } from "./context/TenantContext";
// EVENTSFLO-CREW-PWA-BUILD-VERSION-AND-AUTO-UPDATE-V1: service-worker coordination
import { registerSw } from "./lib/registerSw";
// EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1
import SwUpdateToast from "./components/system/SwUpdateToast";
import LoginPage from "./pages/LoginPage";
// EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1
import RequestQuotePage from "./pages/RequestQuotePage";
// EVENTSFLO-PUBLIC-QUOTE-AUTO-CLIENT-VENUE-MATCH-SUGGESTIONS-V1
import AdminPublicQuoteRequestsPage from "./pages/AdminPublicQuoteRequestsPage";
// EVENTSFLO-JOB-REQUEST-SOURCE-ARCHIVE-V1
import AdminJobRequestArchivePage from "./pages/AdminJobRequestArchivePage";
// EF-SECURE-ONBOARDING-AND-PASSWORD-RESET-LINKS-V1: Secure link pages
import CrewOnboardingPage from "./pages/CrewOnboardingPage";
import PasswordResetPage from "./pages/PasswordResetPage";
// EF-FIX-CREW-ONBOARDING-FLOW-END-TO-END-V1: Simple crew portal
import CrewPortalPage from "./pages/CrewPortalPage";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import VenuesPage from "./pages/VenuesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ShiftsPage from "./pages/ShiftsPage";
import ShiftTasksPage from "./pages/ShiftTasksPage";
import TaskDefinitionsPage from "./pages/TaskDefinitionsPage";
import StaffPage from "./pages/StaffPage";
import StaffImportPage from "./pages/StaffImportPage";
import AssignmentsPage from "./pages/AssignmentsPage";
import AssignmentBoardPage from "./pages/AssignmentBoardPage";
import RulesPage from "./pages/RulesPage";
import QuotesPage from "./pages/QuotesPage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import CreditNoteDetailPage from "./pages/CreditNoteDetailPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
// EVENTSFLO-TENANT-EDITABLE-EMAIL-TEMPLATES-BILINGUAL-V1
import PortalEmailTemplatesPage from "./pages/PortalEmailTemplatesPage";
// EVENTSFLO-TASK-DEFINITIONS-PHASE-A-RENAME-CONFIRMATION-V1
import TaskDefinitionBilingualRenamePage from "./pages/TaskDefinitionBilingualRenamePage";
import TaskDefinitionDependencyReportPage from "./pages/TaskDefinitionDependencyReportPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import DataImportPage from "./pages/DataImportPage";
import SystemInfoPage from "./pages/SystemInfoPage";
import DataCleanupPage from "./pages/DataCleanupPage";
import TestDataCleanupPage from "./pages/TestDataCleanupPage";
import ProjectCalendarPage from "./pages/ProjectCalendarPage";
import StaffCalendarPage from "./pages/StaffCalendarPage";
import PlatformSettingsPage from "./pages/PlatformSettingsPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import MyAssignmentsPage from "./pages/MyAssignmentsPage";
// Crew PWA Pages
// EVENTSFLO-CREW-PWA-ROUTE-REDIRECT-AND-DEPRECATION-GUARD-V1:
// Deprecated `/crew-pwa/*` PWA tree is no longer mounted. All routes redirect
// to `/crew` (CrewPortalPage). Component files in `./pages/crew/*` and
// `./components/crew/CrewLayout` remain on disk (warn on import in dev) and
// will be removed in a future refactor.
import DebugToolsPage from "./pages/DebugToolsPage";
import LogViewerPage from "./pages/LogViewerPage";
import TaskTypesPage from "./pages/TaskTypesPage";
import SheetCrewPlannerPage from "./pages/SheetCrewPlannerPage";
// EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Import deprecated old planner (hidden route only)
import OldTaskCrewPlannerPage from "./pages/OldTaskCrewPlannerPage";
import ParserAliasPage from "./pages/ParserAliasPage";
// EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
import FinancialTaxRulesPage from "./pages/FinancialTaxRulesPage";
import InboxPage from "./pages/InboxPage";
// EVENTSFLO-INTEGRITY-SUMMARY-MAIN-NAV-LINK-V1: read-only operator integrity dashboard
import IntegrityPage from "./pages/IntegrityPage";
import CompanySettingsPage from "./pages/CompanySettingsPage";
// EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1
import CompanyDocumentsSettingsPage from "./pages/CompanyDocumentsSettingsPage";
import SystemContactsPage from "./pages/SystemContactsPage";
// EF-CREW-INTAKE-WORKSPACE-V1: Crew Intake Workspace
import CrewIntakePage from "./pages/CrewIntakePage";
// EF-AI-CREW-ONBOARDING-FRONTEND-V1: AI Crew Onboarding
import AICrewOnboardingPage from "./pages/AICrewOnboardingPage";
// EF-SUPERADMIN-TENANT-MGMT-V1: Tenant Management
import TenantManagementPage from "./pages/admin/TenantManagementPage";
// EF-RBAC-COMPLETION-AND-AUDIT-V1: Role Management
import RolesPage from "./pages/admin/RolesPage";
// EVENTSFLO-ADMIN-BACKUP-SUPERADMIN-UI-V1: SuperAdmin Backups page
import BackupsPage from "./pages/admin/BackupsPage";
// EF-CLIENT-PORTAL-UI-V1: Client Portal Pages
import ClientPortalPage from "./pages/client/ClientPortalPage";
import ClientProjectsPage from "./pages/client/ClientProjectsPage";
import ClientProjectDetailPage from "./pages/client/ClientProjectDetailPage";
import ClientShiftsPage from "./pages/client/ClientShiftsPage";
import ClientCrewPage from "./pages/client/ClientCrewPage";
import ClientDocumentsPage from "./pages/client/ClientDocumentsPage";
// EF-CLIENT-ONBOARDING-FRONTEND-V1: Client Auth Pages
import ClientLoginPage from "./pages/client/ClientLoginPage";
import ClientOnboardingPage from "./pages/client/ClientOnboardingPage";
import ClientResetPasswordPage from "./pages/client/ClientResetPasswordPage";
// EF-CLIENT-ROLE-FOUNDATION-V1: Team Management Page
import ClientTeamPage from "./pages/client/ClientTeamPage";
import ClientQuotesPage from "./pages/client/ClientQuotesPage";
import ClientInvoicesPage from "./pages/client/ClientInvoicesPage";
// EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1
import ClientProjectRequestsPage from "./pages/client/ClientProjectRequestsPage";
import ClientProjectRequestNewPage from "./pages/client/ClientProjectRequestNewPage";
import ClientRequestsAdminPage from "./pages/ClientRequestsAdminPage";
// EVENTSFLO-CLIENT-CHANGE-REQUESTS-ADMIN-APPLY-WORKFLOW-V1
import ClientChangeRequestsAdminPage from "./pages/ClientChangeRequestsAdminPage";
// EF-SETTINGS-CONSOLIDATION-V1: Settings Hub
import SettingsHubPage from "./pages/SettingsHubPage";
// EVENTSFLO-AI-SHIFT-RULES-TENANT-ADMIN-MANAGEMENT-V1
import AIShiftRulesPage from "./pages/AIShiftRulesPage";
// EF-SETTINGS-CONSOLIDATION-V1 (PHASE 2): Email Settings Router with Shadow Mode
import EmailSettingsRouter from "./pages/EmailSettingsRouter";
import CompanyEmailPage from "./pages/CompanyEmailPage";
// EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Integrations Page
import CompanyIntegrationsPage from "./pages/CompanyIntegrationsPage";

// EF-TERMINOLOGY-STANDARDISATION-V2: Redirect helper for edit routes with params
function StaffEditRedirect() {
  const { staffId } = useParams();
  return <Navigate to={`/crew-management/${staffId}/edit`} replace />;
}

// EF-PLATFORM-AUTHENTICATION-V1: Protected Route wrapper
// EVENTSFLO-PWA-LOGOUT-ROLE-ROUTING-SECURITY-AUDIT-FIX-V1: role enforcement
function ProtectedRoute({ children, requiredRoles }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If a role list is supplied, redirect non-matching users to a safe
  // landing page for THEIR role rather than letting them see admin data.
  if (Array.isArray(requiredRoles) && requiredRoles.length > 0) {
    const role = user?.role;
    if (!role || !requiredRoles.includes(role)) {
      // Crew identity → crew PWA; everyone else → /login (forces re-auth).
      const target = role === "crew" ? "/crew" : "/login";
      return <Navigate to={target} replace />;
    }
  }

  return children;
}

// EVENTSFLO-CREW-PORTAL-SHARE-LINK-STAFF-FILTER-V1
// Preserve the original query string (e.g. ?staff=…) when redirecting from
// the deprecated /crew-pwa/* tree to /crew, so planner share links keep
// their staff context.
function CrewPwaRedirect() {
  const location = useLocation();
  return <Navigate to={`/crew${location.search || ""}`} replace />;
}

// Inner component to use theme context
function AppContent() {
  const { theme } = useTheme();
  
  return (
    <PlatformSettingsProvider>
      <UserSettingsProvider>
        {/* EF-SUPERADMIN-TENANT-SWITCH-V1: Tenant context for superadmin switching */}
        <TenantContextProvider>
          <CalendarEditProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                  {/* EF-PLATFORM-AUTHENTICATION-V1: Login Route - Public */}
                  <Route path="/login" element={<LoginPage />} />
                  {/* EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1 */}
                  <Route path="/request-quote" element={<RequestQuotePage />} />
                
                {/* EF-SECURE-ONBOARDING-AND-PASSWORD-RESET-LINKS-V1: Public secure link pages */}
                {/* EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V2: Support both /crew/onboarding and /crew/onboard */}
                <Route path="/crew/onboarding" element={<CrewOnboardingPage />} />
                <Route path="/crew/onboard" element={<CrewOnboardingPage />} />
                <Route path="/reset-password" element={<PasswordResetPage />} />
                
                {/* EF-FIX-CREW-ONBOARDING-FLOW-END-TO-END-V1: Simple Crew Portal */}
                <Route path="/crew" element={<CrewPortalPage />} />
                
                {/* EF-CLIENT-ONBOARDING-FRONTEND-V1: Client Auth Routes - Public */}
                <Route path="/client/login" element={<ClientLoginPage />} />
                <Route path="/client/onboarding" element={<ClientOnboardingPage />} />
                <Route path="/client/reset-password" element={<ClientResetPasswordPage />} />
                
                {/* EF-CLIENT-PORTAL-UI-V1: Client Portal Routes */}
                <Route path="/client" element={<ClientPortalPage />}>
                  <Route path="projects" element={<ClientProjectsPage />} />
                  <Route path="projects/:id" element={<ClientProjectDetailPage />} />
                  <Route path="shifts" element={<ClientShiftsPage />} />
                  <Route path="crew" element={<ClientCrewPage />} />
                  <Route path="documents" element={<ClientDocumentsPage />} />
                  {/* EVENTSFLO-CLIENT-PORTAL-PHASE-1.5-QUOTES-INVOICES-V1 */}
                  <Route path="quotes" element={<ClientQuotesPage />} />
                  <Route path="invoices" element={<ClientInvoicesPage />} />
                  {/* EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1 */}
                  <Route path="project-requests" element={<ClientProjectRequestsPage />} />
                  <Route path="project-requests/new" element={<ClientProjectRequestNewPage />} />
                  {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1 */}
                  <Route path="project-requests/:request_id/edit" element={<ClientProjectRequestNewPage />} />
                  {/* EF-CLIENT-ROLE-FOUNDATION-V1: Team Management (Admin only UI) */}
                  <Route path="team" element={<ClientTeamPage />} />
                </Route>
                
                {/* EVENTSFLO-CREW-PWA-ROUTE-REDIRECT-AND-DEPRECATION-GUARD-V1:
                    Hard-redirect deprecated /crew-pwa/* tree to /crew.
                    EVENTSFLO-CREW-PORTAL-SHARE-LINK-STAFF-FILTER-V1: preserve
                    the original query string (e.g. ?staff=…) so planner share
                    links still carry context after the redirect. */}
                <Route path="/crew-pwa" element={<CrewPwaRedirect />} />
                <Route path="/crew-pwa/*" element={<CrewPwaRedirect />} />
                
                {/* Admin Dashboard Routes - Protected */}
                <Route element={<DashboardLayout><Routes><Route path="*" element={null} /></Routes></DashboardLayout>}>
                </Route>
                <Route path="/*" element={
                  <ProtectedRoute requiredRoles={["superadmin", "tenant_admin", "admin"]}>
                    <DashboardLayout>
                      <Routes>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/clients" element={<ClientsPage />} />
                        <Route path="/clients/:clientId" element={<ClientDetailPage />} />
                        <Route path="/venues" element={<VenuesPage />} />
                        <Route path="/projects" element={<ProjectsPage />} />
                        <Route path="/projects/:id" element={<ProjectDetailPage />} />
                        <Route path="/shifts" element={<ShiftsPage />} />
                        <Route path="/shifts/:shiftId/tasks" element={<ShiftTasksPage />} />
                        {/* EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Old planner route redirects to new planner */}
                        <Route path="/task-crew-planner" element={<Navigate to="/planner/sheet" replace />} />
                        {/* EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Hidden debug route for old planner */}
                        <Route path="/old-planner" element={<OldTaskCrewPlannerPage />} />
                        {/* EF-PLANNER-DEPRECATION-AND-RENAMING-V1: Active Crew Planner */}
                        <Route path="/planner/sheet" element={<SheetCrewPlannerPage />} />
                        <Route path="/task-definitions" element={<TaskDefinitionsPage />} />
                        <Route path="/task-types" element={<TaskTypesPage />} />
                        {/* EF-TERMINOLOGY-STANDARDISATION-V2: /crew-management is the primary admin route */}
                        <Route path="/crew-management" element={<StaffPage />} />
                        <Route path="/crew-management/:staffId/edit" element={<StaffPage />} />
                        {/* EF-TERMINOLOGY-STANDARDISATION-V2: Legacy routes redirect to /crew-management */}
                        <Route path="/staff" element={<Navigate to="/crew-management" replace />} />
                        <Route path="/staff/:staffId/edit" element={<StaffEditRedirect />} />
                        <Route path="/crew-admin" element={<Navigate to="/crew-management" replace />} />
                        <Route path="/crew-admin/:staffId/edit" element={<StaffEditRedirect />} />
                        <Route path="/assignments" element={<AssignmentsPage />} />
                        <Route path="/assignment-board" element={<AssignmentBoardPage />} />
                        <Route path="/rules" element={<RulesPage />} />
                        <Route path="/quotes" element={<QuotesPage />} />
                        <Route path="/quotes/:id" element={<QuoteDetailPage />} />
                        <Route path="/invoices" element={<InvoicesPage />} />
                        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
                        <Route path="/credit-notes/:id" element={<CreditNoteDetailPage />} />
                        {/* EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1 */}
                        <Route path="/client-requests" element={<ClientRequestsAdminPage />} />
                        {/* EVENTSFLO-CLIENT-CHANGE-REQUESTS-ADMIN-APPLY-WORKFLOW-V1 */}
                        <Route path="/change-requests" element={<ClientChangeRequestsAdminPage />} />
                        {/* EVENTSFLO-PUBLIC-QUOTE-AUTO-CLIENT-VENUE-MATCH-SUGGESTIONS-V1 */}
                        <Route path="/admin/public-quote-requests" element={<AdminPublicQuoteRequestsPage />} />
                        {/* EVENTSFLO-JOB-REQUEST-SOURCE-ARCHIVE-V1 */}
                        <Route path="/admin/job-request-archive" element={<AdminJobRequestArchivePage />} />
                        
                        {/* EF-SETTINGS-CONSOLIDATION-V1: Settings Hub and Routes */}
                        <Route path="/settings" element={<SettingsHubPage />} />
                        {/* EVENTSFLO-INTEGRITY-SUMMARY-MAIN-NAV-LINK-V1 */}
                        <Route path="/integrity" element={<IntegrityPage />} />
                        {/* EVENTSFLO-AI-SHIFT-RULES-TENANT-ADMIN-MANAGEMENT-V1 */}
                        <Route path="/settings/ai-shift-rules" element={<AIShiftRulesPage />} />
                        
                        {/* EF-SETTINGS-CONSOLIDATION-V1: Route Aliases (backward compatibility) */}
                        {/* These redirect old routes to new locations */}
                        <Route path="/settings/preferences" element={<UserSettingsPage />} />
                        <Route path="/settings/company/profile" element={<CompanyProfilePage />} />
                        {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 2): New unified email page */}
                        <Route path="/settings/company/email" element={<CompanyEmailPage />} />
                        {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Integrations moved to dedicated page */}
                        <Route path="/settings/company/integrations" element={<CompanyIntegrationsPage />} />
                        
                        {/* Original routes (kept for backward compatibility) */}
                        {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 2): Use router with shadow mode */}
                        <Route path="/settings/email" element={<EmailSettingsRouter />} />
                        <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
                        {/* EVENTSFLO-TENANT-EDITABLE-EMAIL-TEMPLATES-BILINGUAL-V1 */}
                        <Route path="/settings/portal-email-templates" element={<PortalEmailTemplatesPage />} />
                        {/* EVENTSFLO-TASK-DEFINITIONS-PHASE-A-RENAME-CONFIRMATION-V1 */}
                        <Route path="/settings/task-definitions/bilingual-renames" element={<TaskDefinitionBilingualRenamePage />} />
                        {/* EVENTSFLO-TASK-DEFINITIONS-EXPORT-DEPENDENCY-REPORT-V1 */}
                        <Route path="/settings/task-definitions/dependency-report" element={<TaskDefinitionDependencyReportPage />} />
                        <Route path="/settings/company" element={<CompanyProfilePage />} />
                        {/* EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1 */}
                        <Route path="/settings/company-documents" element={<CompanyDocumentsSettingsPage />} />
                        <Route path="/settings/staff-import" element={<StaffImportPage />} />
                        <Route path="/settings/data-import" element={<DataImportPage />} />
                        <Route path="/settings/system-info" element={<SystemInfoPage />} />
                        <Route path="/settings/data-cleanup" element={<DataCleanupPage />} />
                        <Route path="/settings/test-data-cleanup" element={<TestDataCleanupPage />} />
                        <Route path="/settings/debug-tools" element={<DebugToolsPage />} />
                        {/* EF-APP-LOG-VIEWER-V1: Application Log Viewer */}
                        <Route path="/debug/logs" element={<LogViewerPage />} />
                        <Route path="/settings/parser-aliases" element={<ParserAliasPage />} />
                        {/* EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1 */}
                        <Route path="/settings/financial-tax-rules" element={<FinancialTaxRulesPage />} />
                        <Route path="/settings/platform" element={<PlatformSettingsPage />} />
                        <Route path="/settings/user" element={<UserSettingsPage />} />
                        {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): company-settings now redirects to My Preferences */}
                        <Route path="/settings/company-settings" element={<Navigate to="/settings/user" replace />} />
                        {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Old integrations route redirects to new location */}
                        <Route path="/settings/integrations/google" element={<Navigate to="/settings/company/integrations" replace />} />
                        {/* System Contacts for Document Sharing */}
                        <Route path="/admin/system-contacts" element={<SystemContactsPage />} />
                        {/* EF-SUPERADMIN-TENANT-MGMT-V1: Tenant Management (Superadmin only) */}
                        <Route path="/admin/tenants" element={<TenantManagementPage />} />
                        {/* EF-RBAC-COMPLETION-AND-AUDIT-V1: Role Management (Superadmin only) */}
                        <Route path="/admin/roles" element={<RolesPage />} />
                        {/* EVENTSFLO-ADMIN-BACKUP-SUPERADMIN-UI-V1: Backups (Superadmin only) */}
                        <Route path="/admin/backups" element={<BackupsPage />} />
                        {/* Inbox */}
                        <Route path="/inbox" element={<InboxPage />} />
                        {/* EF-CREW-INTAKE-WORKSPACE-V1: Crew Intake */}
                        <Route path="/admin/crew-intake" element={<CrewIntakePage />} />
                        {/* EF-AI-CREW-ONBOARDING-FRONTEND-V1: AI Crew Onboarding */}
                        <Route path="/ai-crew-onboarding" element={<AICrewOnboardingPage />} />
                        {/* Calendar Views */}
                        <Route path="/calendar/projects" element={<ProjectCalendarPage />} />
                        {/* EF-TERMINOLOGY-STANDARDISATION-V2: /calendar/crew is now primary */}
                        <Route path="/calendar/crew" element={<StaffCalendarPage />} />
                        <Route path="/calendar/staff" element={<Navigate to="/calendar/crew" replace />} />
                        {/* Crew Portal */}
                        <Route path="/my-assignments" element={<MyAssignmentsPage />} />
                      </Routes>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
              </Routes>
            </BrowserRouter>
            <Toaster 
              position="top-right" 
              theme={theme}
              toastOptions={{
                style: theme === "dark" ? {
                  background: 'hsl(240 10% 9%)',
                  border: '1px solid hsl(240 5% 17%)',
                  color: 'hsl(0 0% 98%)',
                } : {
                  background: 'hsl(0 0% 100%)',
                  border: '1px solid hsl(240 5% 85%)',
                  color: 'hsl(240 10% 3.9%)',
                },
              }}
            />
            {/* EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1 */}
            <SwUpdateToast />
          </div>
        </CalendarEditProvider>
      </TenantContextProvider>
      </UserSettingsProvider>
    </PlatformSettingsProvider>
  );
}

function App() {
  // EVENTSFLO-CREW-PWA-BUILD-VERSION-AND-AUTO-UPDATE-V1:
  //   Boot the service-worker registration + update coordinator.
  useEffect(() => {
    registerSw();
  }, []);
  return (
    <ThemeProvider defaultTheme="system" storageKey="eventsflo-theme">
      <I18nProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
